import { Formik, Form, FormikHelpers } from 'formik'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Info } from 'lucide-react'
import { emailValidationSchema } from '@/pages/Login/validation'
import { Checkbox } from '../ui/Checkbox'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { UserRole } from '@/constants/room'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux/store'
import { fetchTeamsByRoomId, createNewTeam } from '@/redux/asyncThunks/team'

interface FormValues {
  email: string
  role: string
  sendInvite: boolean
  requirePasswordConfiguration: boolean
  teamId: number | null
  newTeamName: string
}

interface InviteFormProps {
  userRole: UserRole
  isGameStarted: boolean
  roomId: string
  onSubmit: (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => Promise<void>
  roleOptions?: { value: string; label: string }[]
  buttonText: string
  disabledTooltipText: string
}

export function InviteForm({
  userRole,
  isGameStarted,
  roomId,
  onSubmit,
  roleOptions = [],
  buttonText,
  disabledTooltipText,
}: InviteFormProps) {
  const dispatch = useDispatch<AppDispatch>()
  const { teams } = useSelector((state: RootState) => state.team)
  const [showNewTeamInput, setShowNewTeamInput] = useState(false)
  const [isCreatingTeam, setIsCreatingTeam] = useState(false)

  useEffect(() => {
    if (roomId && userRole === UserRole.Student) {
      dispatch(fetchTeamsByRoomId(roomId))
    }
  }, [roomId, userRole, dispatch])

  const getInitialValues = (): FormValues => {
    if (userRole === UserRole.Professor) {
      return {
        email: '',
        role: 'Co-professor',
        sendInvite: false,
        requirePasswordConfiguration: true,
        teamId: null,
        newTeamName: '',
      }
    }
    return {
      email: '',
      role: '',
      sendInvite: false,
      requirePasswordConfiguration: true,
      teamId: null,
      newTeamName: '',
    }
  }

  const handleCreateTeam = async (teamName: string): Promise<number | null> => {
    if (!teamName.trim()) {
      return null
    }
    setIsCreatingTeam(true)
    try {
      const result = await dispatch(
        createNewTeam({ roomId, teamData: { name: teamName.trim() } })
      ).unwrap()
      return result.id
    } catch (error) {
      console.error('Failed to create team:', error)
      throw error
    } finally {
      setIsCreatingTeam(false)
    }
  }

  return (
    <div>
      <div className='mb-3'>
        <span className='text-sm font-medium text-[#0F172A]'>
          Individual Invite
        </span>
      </div>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={emailValidationSchema}
        onSubmit={async (values, helpers) => {
          // If creating a new team, create it first before submitting
          if (
            userRole === UserRole.Student &&
            showNewTeamInput &&
            values.newTeamName.trim() &&
            !values.teamId
          ) {
            try {
              const newTeamId = await handleCreateTeam(values.newTeamName)
              if (newTeamId) {
                // Update form values with the new team ID
                const updatedValues = { ...values, teamId: newTeamId }
                await onSubmit(updatedValues, helpers)
              } else {
                helpers.setFieldError('newTeamName', 'Failed to create team')
              }
            } catch (error) {
              helpers.setFieldError('newTeamName', 'Failed to create team')
            }
          } else {
            await onSubmit(values, helpers)
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          isSubmitting,
          setFieldError,
          setFieldValue,
        }) => (
          <Form>
            <div className='space-y-3'>
              <div className='flex space-x-1'>
                <div className='flex-1'>
                  {isGameStarted ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className='w-full'>
                            <Input
                              className='placeholder:text-sm placeholder:text-[#99A2B0] bg-[#F0F0F0] border-gray-300 cursor-not-allowed'
                              placeholder='Enter email here'
                              name='email'
                              value={values.email}
                              onChange={handleChange}
                              disabled={true}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className='p-2 max-w-xs text-white bg-gray-800'>
                          <div className='flex gap-2 items-center'>
                            <Info size={16} />
                            <p>{disabledTooltipText}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <Input
                      className={cn(
                        'placeholder:text-sm placeholder:text-[#99A2B0] bg-[#FAFCFF]',
                        touched.email && errors.email
                          ? 'border-red-500'
                          : 'border-gray-300'
                      )}
                      placeholder='Enter email here'
                      name='email'
                      value={values.email}
                      onChange={handleChange}
                      onFocus={() => {
                        if (touched.email) {
                          setFieldError('email', undefined)
                        }
                      }}
                      disabled={isSubmitting}
                    />
                  )}
                  {touched.email && errors.email && !isGameStarted && (
                    <div className='flex gap-2 mt-1 text-sm text-red-500'>
                      <img src='/icons/alert-circle.svg' alt='Warning' />
                      {errors.email}
                    </div>
                  )}
                </div>
              </div>

              {userRole === UserRole.Professor && (
                <div className='flex-1'>
                  <Select
                    value={values.role}
                    onValueChange={(value) => setFieldValue('role', value)}
                    disabled={isSubmitting || isGameStarted}
                  >
                    <SelectTrigger className='placeholder:text-sm placeholder:text-[#99A2B0] bg-[#FAFCFF] border-gray-300'>
                      <SelectValue placeholder='Select role' />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {userRole === UserRole.Student && !isGameStarted && (
                <div className='space-y-2'>
                  <Select
                    value={values.teamId?.toString() || 'none'}
                    onValueChange={(value) => {
                      if (value === 'new') {
                        setShowNewTeamInput(true)
                        setFieldValue('teamId', null)
                      } else if (value === 'none') {
                        setShowNewTeamInput(false)
                        setFieldValue('teamId', null)
                        setFieldValue('newTeamName', '')
                      } else {
                        setShowNewTeamInput(false)
                        setFieldValue('teamId', parseInt(value))
                        setFieldValue('newTeamName', '')
                      }
                    }}
                    disabled={isSubmitting || isCreatingTeam}
                  >
                    <SelectTrigger className='placeholder:text-sm placeholder:text-[#99A2B0] bg-[#FAFCFF] border-gray-300'>
                      <SelectValue placeholder='Select team (optional)' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>No team</SelectItem>
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id.toString()}>
                          {team.name}
                        </SelectItem>
                      ))}
                      <SelectItem value='new'>+ Create new team</SelectItem>
                    </SelectContent>
                  </Select>
                  {showNewTeamInput && (
                    <div className='flex gap-2'>
                      <Input
                        className='placeholder:text-sm placeholder:text-[#99A2B0] bg-[#FAFCFF] border-gray-300'
                        placeholder='Enter team name'
                        value={values.newTeamName}
                        onChange={(e) =>
                          setFieldValue('newTeamName', e.target.value)
                        }
                        disabled={isSubmitting || isCreatingTeam}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            if (values.newTeamName.trim()) {
                              try {
                                const newTeamId = await handleCreateTeam(
                                  values.newTeamName
                                )
                                if (newTeamId) {
                                  setFieldValue('teamId', newTeamId)
                                  setFieldValue('newTeamName', '')
                                  setShowNewTeamInput(false)
                                }
                              } catch (error) {
                                setFieldError(
                                  'newTeamName',
                                  'Failed to create team'
                                )
                              }
                            }
                          }
                        }}
                      />
                      <Button
                        type='button'
                        onClick={async () => {
                          if (values.newTeamName.trim()) {
                            try {
                              const newTeamId = await handleCreateTeam(
                                values.newTeamName
                              )
                              if (newTeamId) {
                                setFieldValue('teamId', newTeamId)
                                setFieldValue('newTeamName', '')
                                setShowNewTeamInput(false)
                              }
                            } catch (error) {
                              setFieldError(
                                'newTeamName',
                                'Failed to create team'
                              )
                            }
                          }
                        }}
                        disabled={
                          isSubmitting ||
                          isCreatingTeam ||
                          !values.newTeamName.trim()
                        }
                        className='bg-dark-blue hover:bg-dark-blue-hovered'
                      >
                        {isCreatingTeam ? 'Creating...' : 'Create'}
                      </Button>
                      <Button
                        type='button'
                        onClick={() => {
                          setShowNewTeamInput(false)
                          setFieldValue('newTeamName', '')
                          setFieldValue('teamId', null)
                        }}
                        disabled={isSubmitting || isCreatingTeam}
                        variant='outline'
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div
                className={cn(
                  'flex flex-wrap items-center gap-x-4 gap-y-2',
                  userRole === UserRole.Professor ? 'justify-between' : ''
                )}
              >
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='sendInvite'
                    checked={values.sendInvite}
                    onChange={(e) =>
                      setFieldValue('sendInvite', e.target.checked)
                    }
                    disabled={isGameStarted || isSubmitting}
                  />
                  <label
                    htmlFor='sendInvite'
                    className={cn(
                      'text-sm pt-1 cursor-pointer',
                      isGameStarted ? 'text-gray-400' : 'text-gray-700'
                    )}
                  >
                    Send invite email
                  </label>
                </div>

                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='requirePasswordConfiguration'
                    checked={values.requirePasswordConfiguration}
                    onChange={(e) =>
                      setFieldValue(
                        'requirePasswordConfiguration',
                        e.target.checked
                      )
                    }
                    disabled={isGameStarted || isSubmitting}
                  />
                  <label
                    htmlFor='requirePasswordConfiguration'
                    className={cn(
                      'text-sm pt-1 cursor-pointer',
                      isGameStarted ? 'text-gray-400' : 'text-gray-700'
                    )}
                  >
                    Require Password Configuration
                  </label>
                </div>

                {userRole === UserRole.Professor ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type='submit'
                          className={cn(
                            isGameStarted
                              ? 'bg-gray-400 cursor-not-allowed hover:bg-gray-400'
                              : 'bg-dark-blue hover:bg-dark-blue-hovered'
                          )}
                          disabled={isSubmitting || isGameStarted}
                        >
                          {buttonText}
                        </Button>
                      </TooltipTrigger>
                      {isGameStarted && (
                        <TooltipContent className='p-2 max-w-xs text-white bg-gray-800'>
                          <div className='flex gap-2 items-center'>
                            <Info size={16} />
                            <p>{disabledTooltipText}</p>
                          </div>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                ) : null}
              </div>

              {userRole !== UserRole.Professor && (
                <div className='flex justify-center space-x-1'>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type='submit'
                          className={cn(
                            isGameStarted
                              ? 'bg-gray-400 cursor-not-allowed hover:bg-gray-400'
                              : 'bg-dark-blue hover:bg-dark-blue-hovered'
                          )}
                          disabled={isSubmitting || isGameStarted}
                        >
                          {buttonText}
                        </Button>
                      </TooltipTrigger>
                      {isGameStarted && (
                        <TooltipContent className='p-2 max-w-xs text-white bg-gray-800'>
                          <div className='flex gap-2 items-center'>
                            <Info size={16} />
                            <p>{disabledTooltipText}</p>
                          </div>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
